/** @jsxImportSource @emotion/react */
import { CSSObject } from '@emotion/react';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PercixLogo from './../../assets/percix-logo.png';
import MailIcon from './../../assets/mail.svg';
import SupportIcon from './../../assets/support.svg';
import SettingsIcon from './../../assets/settings.svg';
import { colors } from '../../style/colors';
import UserMenu from './UserMenu';
import { fonts } from '../../style/fonts';
import SupportModal from '../support/SupportModal';
import ContactUsModal from '../contact-us/ContactUsModal';
import { ContactCategoryValue } from '../contact-us/categories';
import { usePerciLauncher } from '../patient-details/treatment-plans/plan-chat/perciLauncher';
import { getUserRoleSelector } from '../../state-manager/selectors/appSelectors';
import { useSelector } from 'react-redux';
import { breakpoints, mq } from '../../style/breakpoints';
import { getIsExtensionModeSelector } from '../../state-manager/selectors/emrSelectors';
export const MAIN_HEADER_HEIGHT = '60px';

const LOGO_WIDTH = 150;
// Mobile: the 180px logo block would eat half of a 360px phone. 120px keeps
// the blue strip usable; the svg scales at exactly 2/3 (159:30 = 106:20).
const LOGO_WIDTH_MOBILE = 120;

// support.svg is the direct child iconsContainerCss sizes; the other two sit
// inside a clickable wrapper, so their svg has to fill that box itself. Each
// icon's viewBox is cropped to its own artwork, so filling the box puts all
// three on the same live area and they read as one size.
const iconFillsBoxCss: CSSObject = {
  '& > svg': { width: '100%', height: '100%' },
};

const supportButtonCss: CSSObject = {
  '&:hover': {
    cursor: 'pointer',
  },

  '&:active': {
    cursor: 'default',
  },
};

// The header icons are buttons, so Tab reaches them and Enter/Space open them.
const iconButtonCss: CSSObject = {
  ...supportButtonCss,
  ...iconFillsBoxCss,
  background: 'transparent',
  border: 'none',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
};

// Exported for the mobile-layer placement/fingerprint tests only.
export const blueAreaCss: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',

  width: `calc(100% - ${LOGO_WIDTH}px)`,
  height: '100%',
  // Starts a step toward the logo-blue strip beside it (was #025CBD) so the two blend.
  background:
    'linear-gradient(90deg, #0F72C5 0%, #0155AF 38.6%, #0052AB 72.06%, ' +
    `${colors.navy} 99.99%)`,
  padding: '0 10px',
  '.tabs-container > button': {
    fontSize: fonts.navTab?.fontSize,
    color: '#B8EEFF',
    '&:hover, &.selected': {
      color: colors.white,
    },
  },

  [breakpoints.small]: {
    paddingRight: 30,
  },

  [mq.mobile]: {
    width: `calc(100% - ${LOGO_WIDTH_MOBILE}px)`,
  },
};

// Exported for the mobile-layer placement/fingerprint tests only.
export const logoBlockCss: CSSObject = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: LOGO_WIDTH,
  height: '100%',
  backgroundColor: colors.white,
  // The strip between the white block and the blue bar, in the logo's blue.
  borderRight: `6px solid ${colors.blue4}`,
  // The PerciX logo from percix.ai (assets/percix-logo.png, 635x203). Sized by height so its
  // aspect ratio holds: 34px tall is ~106px wide, inside the 150px white block.
  img: { height: 34, width: 'auto', display: 'block' },

  [mq.mobile]: {
    width: LOGO_WIDTH_MOBILE,
    flexShrink: 0,
    // ~88px wide, inside the 120px mobile block.
    img: { height: 28 },
  },
};

const iconsContainerCss: CSSObject = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
  minWidth: 0,

  [breakpoints.small]: {
    gap: 14,
  },

  '& > *': {
    height: 24,
    width: 24,

    [breakpoints.small]: {
      width: '26px',
      height: '26px',
    },
  },
};

export const TUTORIALS_URL = 'https://percix-tutorials.netlify.app/';

const tutorialsLinkCss: CSSObject = {
  color: colors.white,
  fontSize: 16,
  fontWeight: 500,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    // The light blue the header icons (mail.svg, support.svg, ...) are drawn in.
    color: '#B8EEFF',
  },

  // Doubled to beat iconsContainerCss's `& > *` square sizing, which ties at (0,1,0).
  '&&': {
    width: 'auto',
    height: 'auto',
  },
};

export default function MainHeader() {
  const [supportModalIsOpen, setSupportModalIsOpen] = useState<boolean>(false);
  const [contactUsModalIsOpen, setContactUsModalIsOpen] =
    useState<boolean>(false);
  // Set when Contact us is opened from the Support window, so it starts on Ask a question.
  const [contactUsCategory, setContactUsCategory] = useState<
    ContactCategoryValue | undefined
  >(undefined);
  const openPerci = usePerciLauncher();
  const history = useHistory();

  const role = useSelector(getUserRoleSelector);
  const isExtensionMode = useSelector(getIsExtensionModeSelector);

  return (
    <>
      <div
        css={{
          position: 'relative',
          cursor: 'pointer',
          display: 'flex',
          height: MAIN_HEADER_HEIGHT,
        }}
      >
        <div css={logoBlockCss}>
          <img
            src={PercixLogo}
            alt="PerciX"
            onClick={() => {
              if (role === 'patient' || isExtensionMode === true) {
                return;
              }
              history.push('/patients');
            }}
          />
        </div>
        <div css={blueAreaCss}>
          <span
            css={{
              ...fonts.bannerFont,
              color: colors.white,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            PT-Copilot
          </span>
          <div css={iconsContainerCss}>
            <a
              href={TUTORIALS_URL}
              target="_blank"
              rel="noopener noreferrer"
              css={tutorialsLinkCss}
            >
              Tutorials
            </a>
            <button
              type="button"
              css={iconButtonCss}
              title="Contact us"
              onClick={() => {
                setContactUsCategory(undefined);
                setContactUsModalIsOpen(true);
              }}
            >
              <MailIcon />
            </button>
            {role !== 'patient' && (
              <button
                type="button"
                css={iconButtonCss}
                title="Support"
                onClick={() => setSupportModalIsOpen(true)}
              >
                <SupportIcon />
              </button>
            )}
            {role !== 'patient' && (
              <button
                type="button"
                css={iconButtonCss}
                title="Settings"
                onClick={() => history.push('/settings')}
              >
                <SettingsIcon />
              </button>
            )}
            <UserMenu />
          </div>
        </div>
      </div>
      <SupportModal
        isOpen={supportModalIsOpen}
        setModalMode={setSupportModalIsOpen}
        onAskPerci={
          openPerci
            ? () => {
                setSupportModalIsOpen(false);
                openPerci();
              }
            : undefined
        }
        onContactUs={() => {
          setSupportModalIsOpen(false);
          setContactUsCategory('QUESTION');
          setContactUsModalIsOpen(true);
        }}
      />
      <ContactUsModal
        isOpen={contactUsModalIsOpen}
        setOpen={setContactUsModalIsOpen}
        initialCategory={contactUsCategory}
      />
    </>
  );
}
